import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import { hoyEc } from '../lib/fechas';

// ── Types ──────────────────────────────────────────────────────────────────────

type InspectionStatus = 'en_progreso' | 'finalizada';
type ResponseValue    = 'cumple' | 'no_cumple' | 'no_aplica';

interface Inspection {
  id:              string;
  company_id:      string;
  inspection_date: string;
  inspector_name:  string | null;
  warehouse_id:    string | null;
  status:          InspectionStatus;
  notes:           string | null;
  created_by:      string | null;
  created_at:      string;
  updated_at:      string;
}

interface InspectionChecklistItem {
  id:              string;
  category:        string;
  item_text:       string;
  legal_reference: string | null;
  display_order:   number;
}

interface WarehouseOption { id: string; name: string; }
interface ResponseSummary  { inspection_id: string; response: ResponseValue | null; }

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<InspectionStatus, string> = {
  en_progreso: 'En progreso',
  finalizada:  'Finalizada',
};

const STATUS_COLORS: Record<InspectionStatus, { bg: string; color: string; border: string }> = {
  en_progreso: { bg: 'rgba(59,130,246,0.12)',  color: '#60a5fa', border: 'rgba(59,130,246,0.3)'  },
  finalizada:  { bg: 'rgba(47,191,113,0.12)',  color: '#2FBF71', border: 'rgba(47,191,113,0.3)'  },
};

const RESP_LABELS: Record<ResponseValue, string> = {
  cumple:    'Cumple',
  no_cumple: 'No cumple',
  no_aplica: 'No aplica',
};

const RESP_ACTIVE: Record<ResponseValue, React.CSSProperties> = {
  cumple:    { background: 'rgba(47,191,113,0.2)',  color: '#2FBF71', borderColor: '#2FBF71' },
  no_cumple: { background: 'rgba(239,68,68,0.2)',   color: '#ef4444', borderColor: '#ef4444' },
  no_aplica: { background: 'rgba(100,116,139,0.2)', color: '#94a3b8', borderColor: '#94a3b8' },
};

const RESP_INACTIVE: React.CSSProperties = {
  background: 'transparent', color: '#475569', borderColor: '#334155',
};

function makeEmptyForm() {
  return {
    inspection_date: hoyEc(),
    inspector_name:  '',
    warehouse_id:    '',
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function InspectionsPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';
  const isSuperAdmin         = profile?.role === 'superadmin';

  // ── State ─────────────────────────────────────────────────────────────────

  const [view,             setView]             = useState<'list' | 'checklist'>('list');
  const [activeInspection, setActiveInspection] = useState<Inspection | null>(null);

  const [inspections,  setInspections]  = useState<Inspection[]>([]);
  const [warehouses,   setWarehouses]   = useState<WarehouseOption[]>([]);
  const [allResponses, setAllResponses] = useState<ResponseSummary[]>([]);
  const [loading,      setLoading]      = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [form,       setForm]       = useState(makeEmptyForm);
  const [creating,   setCreating]   = useState(false);

  const [checklistItems,   setChecklistItems]   = useState<InspectionChecklistItem[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [responseState,    setResponseState]    = useState<Record<string, { response: ResponseValue | null; observation: string }>>({});
  const [savingItem,       setSavingItem]       = useState<Record<string, boolean>>({});
  const [expandedCats,     setExpandedCats]     = useState<Set<string>>(new Set());
  const [finalizing,       setFinalizing]       = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Inspection | null>(null);
  const [deleteStep,   setDeleteStep]   = useState(1);
  const [deleting,     setDeleting]     = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const [{ data: insp }, { data: wh }, { data: resps }] = await Promise.all([
      supabase.from('inspections').select('*').eq('company_id', companyId).order('inspection_date', { ascending: false }),
      supabase.from('warehouses').select('id, name').eq('company_id', companyId).order('name'),
      supabase.from('inspection_responses').select('inspection_id, response').eq('company_id', companyId),
    ]);
    setInspections((insp ?? []) as Inspection[]);
    setWarehouses((wh ?? []) as WarehouseOption[]);
    setAllResponses((resps ?? []) as ResponseSummary[]);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Plan gate (after all hooks) ──────────────────────────────────────────────

  if (!isSuperAdmin && displayCompany && !hasPlanAccess(displayCompany.plan, 'inspecciones')) {
    return <UpgradeWall {...PLAN_FEATURES.inspecciones} />;
  }

  const canEdit   = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const canDelete = profile?.role === 'admin' || !!impersonated;

  // ── Helpers ──────────────────────────────────────────────────────────────────

  function calcCompliance(inspectionId: string): number | null {
    const resps      = allResponses.filter(r => r.inspection_id === inspectionId);
    const applicable = resps.filter(r => r.response !== 'no_aplica');
    if (applicable.length === 0) return null;
    return Math.round((applicable.filter(r => r.response === 'cumple').length / applicable.length) * 100);
  }

  // ── Checklist open ───────────────────────────────────────────────────────────

  async function openChecklist(inspection: Inspection) {
    setActiveInspection(inspection);
    setView('checklist');
    setChecklistLoading(true);
    const [{ data: items }, { data: resps }] = await Promise.all([
      supabase.from('inspection_checklist_items').select('*').order('display_order'),
      supabase.from('inspection_responses').select('*').eq('inspection_id', inspection.id),
    ]);
    const itemList = (items ?? []) as InspectionChecklistItem[];
    setChecklistItems(itemList);

    const map: Record<string, { response: ResponseValue | null; observation: string }> = {};
    (resps ?? []).forEach((r: any) => {
      map[r.checklist_item_id] = { response: r.response as ResponseValue | null, observation: r.observation ?? '' };
    });
    itemList.forEach(item => {
      if (!map[item.id]) map[item.id] = { response: null, observation: '' };
    });
    setResponseState(map);
    setExpandedCats(new Set(itemList.map(i => i.category)));
    setChecklistLoading(false);
  }

  // ── Create ───────────────────────────────────────────────────────────────────

  async function handleCreate() {
    if (!form.inspection_date) { alert('La fecha de inspección es requerida.'); return; }
    setCreating(true);
    const { data, error } = await supabase
      .from('inspections')
      .insert({
        company_id:      companyId,
        inspection_date: form.inspection_date,
        inspector_name:  form.inspector_name.trim() || null,
        warehouse_id:    form.warehouse_id || null,
        status:          'en_progreso',
        created_by:      profile?.id ?? null,
      })
      .select('*')
      .single();
    if (error) { alert('Error al crear inspección: ' + error.message); setCreating(false); return; }
    setCreating(false);
    setShowCreate(false);
    setForm(makeEmptyForm());
    await loadData();
    openChecklist(data as Inspection);
  }

  // ── Checklist responses ──────────────────────────────────────────────────────

  async function handleSelectResponse(itemId: string, value: ResponseValue) {
    if (!activeInspection || !canEdit) return;
    const prev = responseState[itemId] ?? { response: null, observation: '' };
    setResponseState(s => ({ ...s, [itemId]: { ...s[itemId], response: value } }));
    setSavingItem(s => ({ ...s, [itemId]: true }));

    const { error } = await supabase
      .from('inspection_responses')
      .upsert({
        inspection_id:     activeInspection.id,
        checklist_item_id: itemId,
        company_id:        companyId,
        response:          value,
        observation:       responseState[itemId]?.observation || null,
      }, { onConflict: 'inspection_id,checklist_item_id' });

    setSavingItem(s => ({ ...s, [itemId]: false }));
    if (error) {
      alert('Error al guardar respuesta: ' + error.message);
      setResponseState(s => ({ ...s, [itemId]: prev }));
    }
  }

  async function handleObservationBlur(itemId: string, observation: string) {
    if (!activeInspection || !canEdit) return;
    const current = responseState[itemId];
    if (!current?.response) return;

    setSavingItem(s => ({ ...s, [itemId]: true }));
    const { error } = await supabase
      .from('inspection_responses')
      .upsert({
        inspection_id:     activeInspection.id,
        checklist_item_id: itemId,
        company_id:        companyId,
        response:          current.response,
        observation:       observation.trim() || null,
      }, { onConflict: 'inspection_id,checklist_item_id' });
    setSavingItem(s => ({ ...s, [itemId]: false }));
    if (error) alert('Error al guardar observación: ' + error.message);
  }

  async function handleFinalize() {
    if (!activeInspection) return;
    setFinalizing(true);
    const { error } = await supabase
      .from('inspections')
      .update({ status: 'finalizada', updated_at: new Date().toISOString() })
      .eq('id', activeInspection.id);
    setFinalizing(false);
    if (error) { alert('Error al finalizar inspección: ' + error.message); return; }
    setActiveInspection(prev => prev ? { ...prev, status: 'finalizada' } : prev);

    const [y, m, d] = activeInspection.inspection_date.split('-').map(Number);
    const nextDate = new Date(y, (m - 1) + 3, d);
    const nextDateIso = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-${String(nextDate.getDate()).padStart(2, '0')}`;
    await supabase.from('sso_calendar_events').insert({
      company_id:     activeInspection.company_id,
      title:          'Próxima inspección AM 196',
      event_type:     'inspeccion',
      due_date:       nextDateIso,
      assigned_to:    null,
      notes:          null,
      status:         'pendiente',
      completed_date: null,
    });

    loadData();
  }

  // ── Delete ───────────────────────────────────────────────────────────────────

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error: respErr } = await supabase
      .from('inspection_responses')
      .delete()
      .eq('inspection_id', deleteTarget.id);
    if (respErr) { alert('Error al eliminar respuestas: ' + respErr.message); setDeleting(false); return; }
    const { error } = await supabase.from('inspections').delete().eq('id', deleteTarget.id);
    if (error) { alert('Error al eliminar inspección: ' + error.message); setDeleting(false); return; }
    setDeleting(false);
    setDeleteTarget(null);
    setDeleteStep(1);
    loadData();
  }

  // ── Checklist computed ───────────────────────────────────────────────────────

  const answeredCount = checklistItems.filter(i => responseState[i.id]?.response != null).length;
  const totalItems    = checklistItems.length;
  const progress      = totalItems > 0 ? Math.round((answeredCount / totalItems) * 100) : 0;
  const canFinalize   = canEdit && answeredCount === totalItems && totalItems > 0 && activeInspection?.status === 'en_progreso';
  const categories    = Array.from(new Set(checklistItems.map(i => i.category)));

  // ── Checklist view ───────────────────────────────────────────────────────────

  if (view === 'checklist' && activeInspection) {
    const sc = STATUS_COLORS[activeInspection.status];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <button
            onClick={() => { setView('list'); setActiveInspection(null); }}
            style={{ ...ghostBtn, padding: '7px 14px' }}
          >
            ← Volver
          </button>
          <div style={{ flex: 1 }}>
            <h2 style={{ color: '#f1f5f9', fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>
              Checklist AM 196 Anexo 1
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '2px 0 0' }}>
              {new Date(activeInspection.inspection_date + 'T00:00:00').toLocaleDateString('es-EC')}
              {activeInspection.inspector_name ? ` · ${activeInspection.inspector_name}` : ''}
            </p>
          </div>
          <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: 6, padding: '4px 12px', fontSize: '0.8rem', fontWeight: 700 }}>
            {STATUS_LABELS[activeInspection.status]}
          </span>
          {canFinalize && (
            <button onClick={handleFinalize} disabled={finalizing} style={{ ...primaryBtn, opacity: finalizing ? 0.7 : 1 }}>
              {finalizing ? 'Finalizando…' : 'Finalizar inspección'}
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '14px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Progreso del checklist</span>
            <span style={{ color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 700 }}>{answeredCount}/{totalItems} ítems respondidos</span>
          </div>
          <div style={{ background: '#0f172a', borderRadius: 4, height: 8, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: progress === 100 ? '#2FBF71' : '#3b82f6', borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
        </div>

        {checklistLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando checklist…</div>
        ) : (
          <>
            {categories.map(cat => {
              const catItems    = checklistItems.filter(i => i.category === cat);
              const isExpanded  = expandedCats.has(cat);
              const catAnswered = catItems.filter(i => responseState[i.id]?.response != null).length;
              return (
                <div key={cat} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>

                  <button
                    onClick={() => setExpandedCats(s => {
                      const next = new Set(s);
                      if (next.has(cat)) next.delete(cat); else next.add(cat);
                      return next;
                    })}
                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <span style={{ color: '#e2e8f0', fontSize: '0.92rem', fontWeight: 700 }}>{cat}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ color: catAnswered === catItems.length ? '#2FBF71' : '#64748b', fontSize: '0.76rem', fontWeight: 600 }}>
                        {catAnswered}/{catItems.length}
                      </span>
                      <span style={{ color: '#64748b', fontSize: '0.75rem' }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div style={{ borderTop: '1px solid #334155' }}>
                      {catItems.map((item, idx) => {
                        const rs       = responseState[item.id] ?? { response: null, observation: '' };
                        const isSaving = savingItem[item.id] ?? false;
                        const isLocked = !canEdit || activeInspection.status === 'finalizada';
                        return (
                          <div
                            key={item.id}
                            style={{
                              padding: '16px 20px',
                              borderBottom: idx < catItems.length - 1 ? '1px solid rgba(51,65,85,0.5)' : 'none',
                              background: rs.response === 'cumple' ? 'rgba(47,191,113,0.03)' : rs.response === 'no_cumple' ? 'rgba(239,68,68,0.03)' : 'transparent',
                              transition: 'background 0.2s',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, minWidth: 200 }}>
                                <p style={{ color: '#e2e8f0', fontSize: '0.88rem', margin: '0 0 4px', lineHeight: 1.5 }}>
                                  {item.item_text}
                                  {isSaving && <span style={{ color: '#475569', fontSize: '0.72rem', marginLeft: 8 }}>guardando…</span>}
                                </p>
                                {item.legal_reference && (
                                  <p style={{ color: '#475569', fontSize: '0.74rem', margin: 0 }}>{item.legal_reference}</p>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                {(['cumple', 'no_cumple', 'no_aplica'] as ResponseValue[]).map(val => (
                                  <button
                                    key={val}
                                    onClick={() => handleSelectResponse(item.id, val)}
                                    disabled={isLocked || isSaving}
                                    style={{
                                      padding: '5px 10px', borderRadius: 6, border: '1px solid',
                                      cursor: isLocked || isSaving ? 'default' : 'pointer',
                                      fontSize: '0.76rem', fontWeight: 600, transition: 'all 0.15s',
                                      ...(rs.response === val ? RESP_ACTIVE[val] : RESP_INACTIVE),
                                    }}
                                  >
                                    {RESP_LABELS[val]}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <input
                              style={{ ...obsInp, marginTop: 10 }}
                              placeholder="Observación (opcional)…"
                              value={rs.observation}
                              disabled={isLocked}
                              onChange={e => setResponseState(s => ({ ...s, [item.id]: { ...s[item.id], observation: e.target.value } }))}
                              onBlur={e => handleObservationBlur(item.id, e.target.value)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {canEdit && activeInspection.status === 'en_progreso' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
                <button
                  onClick={handleFinalize}
                  disabled={!canFinalize || finalizing}
                  style={{
                    ...primaryBtn,
                    opacity: (!canFinalize || finalizing) ? 0.5 : 1,
                    cursor: !canFinalize ? 'not-allowed' : 'pointer',
                  }}
                  title={!canFinalize ? `Responde todos los ítems para finalizar (${answeredCount}/${totalItems})` : undefined}
                >
                  {finalizing ? 'Finalizando…' : `Finalizar inspección (${answeredCount}/${totalItems})`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Inspecciones SST</h2>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
            Verificación de condiciones según AM 196 Anexo 1 — {inspections.length} inspección{inspections.length !== 1 ? 'es' : ''} registrada{inspections.length !== 1 ? 's' : ''}
          </p>
        </div>
        {canEdit && (
          <button onClick={() => { setForm(makeEmptyForm()); setShowCreate(true); }} style={primaryBtn}>
            + Nueva inspección
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando…</div>
        ) : inspections.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
            Aún no hay inspecciones registradas.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Fecha', 'Inspector', 'Bodega', 'Estado', 'Cumplimiento', ''].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inspections.map(insp => {
                  const sc         = STATUS_COLORS[insp.status];
                  const whName     = warehouses.find(w => w.id === insp.warehouse_id)?.name ?? '—';
                  const compliance = calcCompliance(insp.id);
                  return (
                    <tr key={insp.id}
                      style={{ borderBottom: '1px solid rgba(51,65,85,0.5)', transition: 'background 0.1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>

                      <td style={{ ...td, color: '#cbd5e1', whiteSpace: 'nowrap' }}>
                        {new Date(insp.inspection_date + 'T00:00:00').toLocaleDateString('es-EC')}
                      </td>
                      <td style={{ ...td, color: '#94a3b8' }}>{insp.inspector_name ?? '—'}</td>
                      <td style={{ ...td, color: '#94a3b8' }}>{whName}</td>
                      <td style={td}>
                        <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {STATUS_LABELS[insp.status]}
                        </span>
                      </td>
                      <td style={td}>
                        {compliance !== null ? (
                          <span style={{ color: compliance >= 80 ? '#2FBF71' : compliance >= 60 ? '#fbbf24' : '#ef4444', fontWeight: 700, fontSize: '0.88rem' }}>
                            {compliance}%
                          </span>
                        ) : (
                          <span style={{ color: '#475569', fontSize: '0.78rem' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button onClick={() => openChecklist(insp)} style={ghostBtn}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#60a5fa'; e.currentTarget.style.color = '#60a5fa'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}>
                            {insp.status === 'finalizada' ? 'Ver' : 'Continuar'}
                          </button>
                          {canDelete && (
                            <button onClick={() => { setDeleteTarget(insp); setDeleteStep(1); }} style={ghostBtn}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}>
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inspections.length > 0 && (
        <p style={{ color: '#475569', fontSize: '0.78rem', textAlign: 'right', margin: 0 }}>
          {inspections.length} inspección{inspections.length !== 1 ? 'es' : ''} registrada{inspections.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* ── Create Modal ─────────────────────────────────────────────────────── */}
      {showCreate && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !creating && setShowCreate(false)}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Nueva inspección</h2>
              <button onClick={() => !creating && setShowCreate(false)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 14, marginTop: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Fecha de inspección *">
                  <input style={inp} type="date" value={form.inspection_date} onChange={e => setForm(f => ({ ...f, inspection_date: e.target.value }))} />
                </Field>
                <Field label="Inspector">
                  <input style={inp} value={form.inspector_name} onChange={e => setForm(f => ({ ...f, inspector_name: e.target.value }))} placeholder="Nombre del inspector" />
                </Field>
              </div>
              <Field label="Bodega (opcional)">
                <select style={sel} value={form.warehouse_id} onChange={e => setForm(f => ({ ...f, warehouse_id: e.target.value }))}>
                  <option value="">— Sin bodega específica —</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </Field>
            </div>
            <div style={modalFooter}>
              <button onClick={() => !creating && setShowCreate(false)} style={cancelBtn}>Cancelar</button>
              <button onClick={handleCreate} disabled={creating} style={{ ...saveBtn, opacity: creating ? 0.7 : 1 }}>
                {creating ? 'Creando…' : 'Crear e iniciar checklist'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Modal ─────────────────────────────────────────────────────── */}
      {deleteTarget && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !deleting && setDeleteTarget(null)}>
          <div style={{ ...modalBox, maxWidth: 420 }}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Eliminar inspección</h2>
              <button onClick={() => !deleting && setDeleteTarget(null)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {deleteStep === 1 ? (
                <>
                  <p style={{ color: '#cbd5e1', fontSize: '0.88rem', margin: '0 0 8px' }}>
                    ¿Eliminar la inspección del{' '}
                    <strong style={{ color: '#f1f5f9' }}>
                      {new Date(deleteTarget.inspection_date + 'T00:00:00').toLocaleDateString('es-EC')}
                    </strong>?
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '0 0 20px' }}>
                    Se eliminarán también todas las respuestas del checklist asociadas a esta inspección.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setDeleteTarget(null)} style={cancelBtn}>Cancelar</button>
                    <button onClick={() => setDeleteStep(2)} style={{ ...saveBtn, background: '#dc2626' }}>Continuar</button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ color: '#fca5a5', fontSize: '0.88rem', fontWeight: 600, margin: '0 0 20px' }}>
                    ⚠ Confirma que deseas eliminar definitivamente esta inspección. Esta acción no se puede deshacer.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setDeleteStep(1)} style={cancelBtn}>Atrás</button>
                    <button onClick={handleDelete} disabled={deleting} style={{ ...saveBtn, background: '#dc2626', opacity: deleting ? 0.7 : 1 }}>
                      {deleting ? 'Eliminando…' : 'Eliminar definitivamente'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const primaryBtn:  React.CSSProperties = { padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer' };
const thStyle:     React.CSSProperties = { padding: '11px 14px', textAlign: 'left', color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td:          React.CSSProperties = { padding: '11px 14px', verticalAlign: 'middle' };
const ghostBtn:    React.CSSProperties = { padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', transition: 'all 0.15s' };
const overlay:     React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: '20px 16px', overflowY: 'auto' };
const modalBox:    React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 24px 48px rgba(0,0,0,0.5)', margin: 'auto' };
const modalHeader: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' };
const modalFooter: React.CSSProperties = { padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' };
const inp:         React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel:         React.CSSProperties = { ...inp, cursor: 'pointer' };
const obsInp:      React.CSSProperties = { ...inp, padding: '7px 10px', fontSize: '0.8rem', color: '#94a3b8' };
const closeBtn:    React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 };
const cancelBtn:   React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };
const saveBtn:     React.CSSProperties = { padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 };
